import { useEffect, useState } from "react";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import {
	getAdminSettings,
	updateAdminSetting,
	type AdminSettings,
} from "@/services/api";

export function Settings() {
	const { toast } = useToast();
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	// Values loaded from the server (used to detect which fields changed)
	const [saved, setSaved] = useState<AdminSettings>({
		stripe_attendance_price_id_monthly: "",
		stripe_attendance_price_id_yearly: "",
	});
	const [monthly, setMonthly] = useState("");
	const [yearly, setYearly] = useState("");

	useEffect(() => {
		getAdminSettings()
			.then((data) => {
				setSaved(data);
				setMonthly(data.stripe_attendance_price_id_monthly);
				setYearly(data.stripe_attendance_price_id_yearly);
			})
			.catch((err) =>
				toast({
					title: "Failed to load settings",
					description: err.message,
					variant: "destructive",
				}),
			)
			.finally(() => setLoading(false));
	}, [toast]);

	const handleSave = async () => {
		setSaving(true);
		try {
			const changes: Array<[keyof AdminSettings, string]> = [];
			if (monthly !== saved.stripe_attendance_price_id_monthly)
				changes.push(["stripe_attendance_price_id_monthly", monthly]);
			if (yearly !== saved.stripe_attendance_price_id_yearly)
				changes.push(["stripe_attendance_price_id_yearly", yearly]);

			if (changes.length === 0) {
				toast({ title: "No changes to save" });
				return;
			}

			for (const [key, value] of changes) {
				await updateAdminSetting(key, value);
			}
			setSaved({
				stripe_attendance_price_id_monthly: monthly,
				stripe_attendance_price_id_yearly: yearly,
			});
			toast({ title: "Settings saved", variant: "success" });
		} catch (err) {
			toast({
				title: "Failed to save settings",
				description: (err as Error).message,
				variant: "destructive",
			});
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-2xl font-bold text-gray-900">Settings</h1>
				<p className="text-gray-500 mt-1">Global system configuration</p>
			</div>

			<Card className="max-w-2xl">
				<CardHeader>
					<CardTitle>Attendance Add-on (Stripe)</CardTitle>
					<CardDescription>
						Use a Stripe multi-currency price (currency_options) so one price ID
						covers all currencies — same as plans.
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="monthly">Monthly price ID</Label>
						<Input
							id="monthly"
							value={monthly}
							onChange={(e) => setMonthly(e.target.value)}
							placeholder="price_..."
							disabled={loading || saving}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="yearly">Yearly price ID</Label>
						<Input
							id="yearly"
							value={yearly}
							onChange={(e) => setYearly(e.target.value)}
							placeholder="price_..."
							disabled={loading || saving}
						/>
					</div>
					<Button onClick={handleSave} disabled={loading || saving}>
						{saving ? "Saving..." : "Save"}
					</Button>
				</CardContent>
			</Card>
		</div>
	);
}
